import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Briefcase, Award, TrendingUp, MapPin, DollarSign, Clock, Send, HardHat, Heart } from 'lucide-react';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    resume: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        message: '',
        resume: null
      });
    }, 1500);
  };

  const benefits = [
    { icon: DollarSign, title: 'Competitive Salary',        description: 'Industry-leading compensation packages with performance bonuses' },
    { icon: Heart,      title: 'Health Benefits',           description: 'Comprehensive medical, dental, and vision insurance for you and your family' },
    { icon: TrendingUp, title: 'Career Growth',             description: 'Continuous training and advancement opportunities within the company' },
    { icon: Award,      title: 'Professional Development',  description: 'Ongoing education and certification programs to enhance your skills' },
    { icon: Clock,      title: 'Work-Life Balance',         description: 'Flexible scheduling and paid time off to support your personal life' },
    { icon: Users,      title: 'Team Culture',              description: 'Collaborative environment with experienced professionals' },
  ];

  const openPositions = [
    {
      title: 'Senior Electrical Engineer',
      type: 'Full-Time',
      location: 'Greater Boston, MA',
      experience: '5+ years',
      description: 'Lead electrical design projects for commercial developments and luxury residential properties.',
      requirements: [
        'Bachelor\'s degree in Electrical Engineering',
        'PE license preferred',
        'Experience with commercial electrical systems',
        'Proficient in AutoCAD and electrical design software'
      ]
    },
    {
      title: 'Master Electrician',
      type: 'Full-Time',
      location: 'MetroWest, MA',
      experience: '7+ years',
      description: 'Oversee electrical installations and manage project teams for large-scale commercial projects.',
      requirements: [
        'Valid Master Electrician license',
        'Experience with commercial installations',
        'Leadership and project management skills',
        'Strong knowledge of electrical codes and safety'
      ]
    },
    {
      title: 'Journeyman Electrician',
      type: 'Full-Time',
      location: 'Greater Boston, MA',
      experience: '3+ years',
      description: 'Install and maintain electrical systems in commercial and residential projects.',
      requirements: [
        'Valid Journeyman Electrician license',
        'Experience with commercial and residential wiring',
        'Ability to read blueprints and schematics',
        'Strong problem-solving skills'
      ]
    },
    {
      title: 'Solar Installation Technician',
      type: 'Full-Time',
      location: 'Massachusetts — Multiple Sites',
      experience: '2+ years',
      description: 'Install and maintain solar power systems for commercial and residential properties.',
      requirements: [
        'Solar installation certification preferred',
        'Experience with PV systems and inverters',
        'Electrical background or training',
        'Comfortable working at heights'
      ]
    },
    {
      title: 'Project Manager',
      type: 'Full-Time',
      location: 'Greater Boston, MA',
      experience: '5+ years',
      description: 'Manage electrical projects from conception to completion, ensuring quality and timeline adherence.',
      requirements: [
        'Bachelor\'s degree in Construction Management or related field',
        'Experience in electrical project management',
        'PMP certification preferred',
        'Strong communication and leadership skills'
      ]
    },
    {
      title: 'Electrical Apprentice',
      type: 'Full-Time',
      location: 'Massachusetts — Multiple Sites',
      experience: 'Entry Level',
      description: 'Learn the electrical trade while working alongside experienced professionals on real projects.',
      requirements: [
        'High school diploma or equivalent',
        'Enrolled in electrical apprenticeship program',
        'Strong work ethic and willingness to learn',
        'Basic electrical knowledge preferred'
      ]
    }
  ];

  return (
    <div>
      {/* Hero */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Join Our Team</motion.p>
          <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-4 max-w-3xl">
            Join Our Team
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            BPR Electric is hiring licensed electricians and field supervisors for commercial projects across Massachusetts. We run organized job sites, pay competitive wages, and carry consistent workloads.
          </motion.p>
        </div>
      </motion.section>

      {/* Why Join Us */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-900"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">What to Expect</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
            What to Expect
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            BPR Electric operates organized job sites with clear leadership structure, consistent project schedules, and competitive compensation.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy-600 mt-16">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="bg-navy-900 p-8"
              >
                <benefit.icon className="h-6 w-6 text-primary-500 mb-6" />
                <h3 className="font-heading text-white uppercase text-xl">{benefit.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Open Positions */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="max-w-7xl mx-auto px-6">
          <motion.p variants={fadeUp} className="eyebrow">Open Positions</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
            Open Positions
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
            We're always looking for talented professionals to join our growing team.
          </motion.p>

          <div className="mt-16 space-y-0">
            {openPositions.map((position, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="border-t border-navy-600 py-8"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-white uppercase text-xl">{position.title}</h3>
                    <div className="flex flex-wrap gap-6 mt-3">
                      <span className="flex items-center text-slate-400 text-sm">
                        <Briefcase className="h-4 w-4 mr-2 text-primary-500" />
                        {position.type}
                      </span>
                      <span className="flex items-center text-slate-400 text-sm">
                        <MapPin className="h-4 w-4 mr-2 text-primary-500" />
                        {position.location}
                      </span>
                      <span className="flex items-center text-slate-400 text-sm">
                        <Users className="h-4 w-4 mr-2 text-primary-500" />
                        {position.experience}
                      </span>
                    </div>
                    <p className="text-slate-400 leading-relaxed mt-4">{position.description}</p>
                    <div className="mt-4">
                      <h4 className="font-heading text-white uppercase text-sm tracking-wide mb-3">Requirements</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start text-slate-400 text-sm">
                            <span className="text-primary-500 mr-2">—</span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Application Form */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-900"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div variants={fadeUp}>
              <p className="eyebrow">Apply Today</p>
              <h2 className="font-heading text-h2 text-white mt-4">Apply Today</h2>
              <p className="text-slate-400 text-lg leading-relaxed mt-6">
                Ready to join our team? Fill out the application form and we'll contact you if your qualifications match our needs.
              </p>

              <div className="bg-navy-700 border border-navy-600 border-l-2 border-l-primary-500 p-8 mt-8">
                <div className="flex items-center mb-4">
                  <HardHat className="h-5 w-5 text-primary-500 mr-3" />
                  <h3 className="font-heading text-white uppercase text-lg">Field-First Organization</h3>
                </div>
                <p className="text-slate-400 leading-relaxed text-sm">
                  Our foremen have real authority on the job site. Work is planned in advance, not
                  improvised. Crew members know what they're doing each day and have a supervisor
                  who is present and accountable. We run commercial work — not service calls.
                </p>
              </div>

              <div className="bg-navy-700 border border-navy-600 p-8 mt-4">
                <h3 className="font-heading text-white uppercase text-lg mb-3">Equal Opportunity Employer</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  BPR Electric is an equal opportunity employer. We celebrate diversity and are committed to creating an inclusive environment for all employees.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp}>
              <div className="bg-navy-800 border border-navy-600 p-8">
                {submitStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="border border-navy-600 p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                      <Users className="h-10 w-10 text-primary-500" />
                    </div>
                    <h3 className="font-heading text-white uppercase text-h3 mb-4">Application Received</h3>
                    <p className="text-slate-400 mb-6">
                      Thank you for your interest in joining BPR Electric. We'll review your application and contact you if there's a match.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('')}
                      className="btn-primary"
                    >
                      Submit Another Application
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Position *
                        </label>
                        <select
                          name="position"
                          value={formData.position}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                        >
                          <option value="">Select a position</option>
                          {openPositions.map((pos) => (
                            <option key={pos.title} value={pos.title}>{pos.title}</option>
                          ))}
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                        Years of Experience *
                      </label>
                      <input
                        type="text"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                        placeholder="e.g., 5+ years in commercial electrical"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                        Cover Letter *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors resize-none placeholder-slate-400"
                        placeholder="Tell us why you're interested in joining BPR Electric..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-b-2 border-white mr-3"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Career;
